/**
 * PujcovnaBranaTool — the firma's card payment gateway on the Správa page (`?nastroj=brana`, P6-a). The gateway is the LESSOR's own
 * merchant, so the money of a car's rentals settles to the owner named on the contract. A version is never edited: new keys or another
 * provider add a version that becomes the active one; the old one stays, for the payments it initiated. The secret fields are write-only —
 * the server stores them encrypted and no screen ever shows them again. Admin only, like the API.
 */

import { useState, type FormEvent } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { useConfirm } from '@/components/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Field } from '@/components/Field';
import { Icon } from '@/components/Icon';
import { Modal, DialogCrown } from '@/components/Modal';
import { SelectInput } from '@/components/SelectInput';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useBranyQuery, useCreateBranaMutation, useVypnoutBranuMutation, type PujcovnaBrana, type PujcovnaPoskytovatel } from './api';
import { METODA_NAZEV, OKNO_MAX, OKNO_MIN, branaBody, branaSeProvidenem, branaSeed, datumCas, parseOkno, type BranaForm } from './brana-helpers';

const FORM_ID = 'pujcovna-brana-form';

function NovaVerzeModal({ poskytovatele, open, onOpenChange }: { poskytovatele: PujcovnaPoskytovatel[]; open: boolean; onOpenChange: (open: boolean) => void }) {
  const create = useCreateBranaMutation();
  const [form, setForm] = useState<BranaForm>(() => branaSeed(poskytovatele[0]!));
  const poskytovatel = poskytovatele.find((p) => p.id === form.provider) ?? poskytovatele[0]!;
  const busy = create.isPending;
  // the dialog cannot be dismissed while the save is in flight (its toasts hang on this observer)
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };
  const okno = parseOkno(form.okno);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const body = branaBody(form, poskytovatel);
    if (!body.ok) {
      toast.error(body.chyba);
      return;
    }
    create.mutate(body.body, {
      onSuccess: () => {
        toast.ok('Platební brána uložena.');
        onOpenChange(false);
      },
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit platební bránu.'}`),
    });
  };

  return (
    <Modal open={open} onOpenChange={guarded} maxWidth={640}>
      <DialogCrown
        eyebrow="Platební brána"
        title={<>Nová <em>verze</em></>}
        sub="Nová verze nahradí tu aktivní. Tajné klíče se uloží šifrovaně a už se nezobrazí."
      />
      <Modal.Body className="px-8 py-5">
        <form id={FORM_ID} onSubmit={submit} data-testid="brana-form" className="flex flex-col gap-4">
          <Field label="Poskytovatel" required>
            <SelectInput
              value={form.provider}
              disabled={busy || poskytovatele.length < 2}
              data-testid="brana-provider"
              onChange={(e) => {
                const p = poskytovatele.find((x) => x.id === e.currentTarget.value);
                if (p) setForm((f) => branaSeProvidenem(f, p));
              }}
            >
              {poskytovatele.map((p) => (
                <option key={p.id} value={p.id}>{p.nazev}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Režim" required hint={form.rezim === 'live' ? 'Ostrý provoz: platby se skutečně strhávají.' : 'Testovací režim brány: platby se nestrhávají.'}>
            <SelectInput value={form.rezim} disabled={busy || poskytovatel.rezimy.length < 2} data-testid="brana-rezim" onChange={(e) => {
                const live = e.currentTarget.value === 'live';
                setForm((f) => ({ ...f, rezim: live ? 'live' : 'test' }));
              }}
            >
              {poskytovatel.rezimy.map((r) => (
                <option key={r} value={r}>{r === 'live' ? 'Ostrý' : 'Testovací'}</option>
              ))}
            </SelectInput>
          </Field>
          {poskytovatel.pole.map((f) => (
            <Field key={f.klic} label={f.popisek} required hint={f.tajne ? 'Uloží se šifrovaně, znovu se nezobrazí.' : undefined}>
              <TextInput
                value={form.hodnoty[f.klic] ?? ''}
                disabled={busy}
                // a secret is typed blind; `new-password` is the one value browsers honour, so the user's own login is never offered into it
                type={f.tajne ? 'password' : 'text'}
                autoComplete={f.tajne ? 'new-password' : 'off'}
                data-testid={`brana-pole-${f.klic}`}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setForm((x) => ({ ...x, hodnoty: { ...x.hodnoty, [f.klic]: value } }));
                }}
              />
            </Field>
          ))}
          <div className="flex flex-col gap-2" role="group" aria-label="Platební metody">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Platební metody</span>
            {poskytovatel.metody.map((m) => (
              <Checkbox
                key={m}
                label={METODA_NAZEV[m] ?? m}
                checked={form.metody.includes(m)}
                disabled={busy}
                data-testid={`brana-metoda-${m}`}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setForm((x) => ({ ...x, metody: on ? [...x.metody, m] : x.metody.filter((y) => y !== m) }));
                }}
              />
            ))}
          </div>
          <Field
            label="Platnost platby kartou"
            required
            hint={okno == null && form.okno.trim() !== '' ? `Zadejte celé číslo od ${OKNO_MIN} do ${OKNO_MAX}.` : 'Jak dlouho zůstane rozpracovaná platba kartou otevřená.'}
            hintTone={okno == null && form.okno.trim() !== '' ? 'error' : 'neutral'}
          >
            <TextInput
              inputMode="numeric"
              value={form.okno}
              suffix="min"
              disabled={busy}
              data-testid="brana-okno"
              onChange={(e) => {
                const okno = e.currentTarget.value; // read before the updater runs: the event's target is gone by then
                setForm((f) => ({ ...f, okno }));
              }}
            />
          </Field>
        </form>
      </Modal.Body>
      <Modal.Foot
        actions={
          <>
            <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
              Zrušit
            </Button>
            <Button variant="ink" type="submit" form={FORM_ID} disabled={busy}>
              Uložit jako novou verzi
            </Button>
          </>
        }
      />
    </Modal>
  );
}

function Radek({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 py-1.5">
      <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">{label}</dt>
      <dd className="text-[13px] text-ink">{children}</dd>
    </div>
  );
}

const nastaveniText = (b: PujcovnaBrana) => Object.entries(b.nastaveni).map(([k, v]) => `${k}: ${v}`).join(', ') || '—';

export function PujcovnaBranaTool() {
  const query = useBranyQuery();
  const vypnout = useVypnoutBranuMutation();
  const { confirm, dialog } = useConfirm();
  const [novaOpen, setNovaOpen] = useState(false);

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-2 px-8 pt-5">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div className="px-8 pb-8 pt-5">
        <EmptyState eyebrow="Chyba" title="Platební bránu se nepodařilo načíst" />
      </div>
    );
  }
  const { brany, poskytovatele, klic_nastaven } = query.data;
  const aktivni = brany.find((b) => b.aktivni);
  const drivejsi = brany.filter((b) => !b.aktivni);
  const lzeUlozit = klic_nastaven && poskytovatele.length > 0;

  const vypnoutAktivni = async (b: PujcovnaBrana) => {
    const ok = await confirm({
      title: <>Vypnout platební bránu?</>,
      description: 'Bez aktivní brány nepůjde platit kartou a vozy majitele nelze nabízet na webu. Rozpracované platby se dokončí podle uložené verze.',
      tone: 'danger',
      confirmLabel: 'Vypnout',
    });
    if (!ok) return;
    vypnout.mutate(b.id, {
      onSuccess: () => toast.ok('Platební brána vypnuta.'),
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se vypnout platební bránu.'}`),
    });
  };

  const columns: DataTableColumn<PujcovnaBrana>[] = [
    { id: 'verze', header: 'Verze', width: '90px', priority: 'primary', cell: (b) => <span className="font-mono text-[12px] text-ink">№ {b.id}</span> },
    { id: 'poskytovatel', header: 'Poskytovatel', width: 'minmax(160px, 1fr)', priority: 'primary', cell: (b) => <span className="text-[13px] text-ink">{b.provider_nazev}</span> },
    { id: 'rezim', header: 'Režim', width: '110px', priority: 'secondary', cell: (b) => <Badge tone={b.rezim === 'live' ? 'ok' : 'neutral'}>{b.rezim === 'live' ? 'Ostrý' : 'Testovací'}</Badge> },
    { id: 'od', header: 'Od', width: '150px', priority: 'secondary', cell: (b) => <span className="font-mono text-[12px] text-ink-3">{datumCas(b.vytvoreno_at)}</span> },
    { id: 'do', header: 'Do', width: '150px', priority: 'secondary', cell: (b) => <span className="font-mono text-[12px] text-ink-3">{b.vypnuto_at ? datumCas(b.vypnuto_at) : '—'}</span> },
  ];

  return (
    <div data-testid="brana-tool" className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5">
      <p className="max-w-[720px] text-[13px] leading-relaxed text-ink-2">
        Platby kartou za pronájem jdou na účet majitele vozu. Bránu zadává správce firmy majitele. Tajné klíče se ukládají šifrovaně a už se nezobrazují; změna klíčů přidá novou verzi, stará zůstane pro platby, které s ní vznikly.
      </p>

      {klic_nastaven && poskytovatele.length === 0 && (
        <p data-testid="brana-bez-poskytovatele" className="max-w-[720px] border border-line bg-paper px-4 py-3 text-[13px] text-ink-2">
          Zatím není k dispozici žádný poskytovatel plateb. Bránu půjde nastavit, až bude napojena platební brána (ČSOB).
        </p>
      )}

      {!klic_nastaven && (
        <p data-testid="brana-bez-klice" className="max-w-[720px] border border-warn bg-warn-soft px-4 py-3 text-[13px] text-ink">
          Server nemá nastavený šifrovací klíč platebních údajů (PLATBY_KLIC), proto je teď nelze bezpečně uložit. Požádejte správce platformy.
        </p>
      )}

      <section className="flex max-w-[720px] flex-col gap-3 border border-line bg-paper p-5" aria-label="Aktivní platební brána">
        {aktivni ? (
          <>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Aktivní brána</span>
              <Badge tone="ok">Aktivní</Badge>
              <Badge tone={aktivni.rezim === 'live' ? 'ok' : 'neutral'}>{aktivni.rezim === 'live' ? 'Ostrý' : 'Testovací'}</Badge>
            </div>
            <dl data-testid="brana-aktivni">
              <Radek label="Poskytovatel">{aktivni.provider_nazev}</Radek>
              <Radek label="Nastavení">{nastaveniText(aktivni)}</Radek>
              <Radek label="Tajné klíče">
                {!aktivni.tajne_citelne ? (
                  <span data-testid="brana-necitelne" className="text-red">
                    Uložené klíče nelze přečíst — šifrovací klíč na serveru se změnil nebo je chybně zadaný. Zadejte bránu znovu jako novou verzi.
                  </span>
                ) : aktivni.tajne_nastavene.length > 0 ? (
                  'Uloženy (nezobrazují se)'
                ) : (
                  '—'
                )}
              </Radek>
              <Radek label="Platební metody">{aktivni.metody.map((m) => METODA_NAZEV[m] ?? m).join(', ')}</Radek>
              <Radek label="Platnost platby kartou">{aktivni.okno_karty_min} min</Radek>
              <Radek label="Platí od">{datumCas(aktivni.vytvoreno_at)}</Radek>
            </dl>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => vypnoutAktivni(aktivni)} disabled={vypnout.isPending}>
                Vypnout
              </Button>
              <Button variant="primary" size="sm" icon={<Icon name="plus" size={12} />} onClick={() => setNovaOpen(true)} disabled={!lzeUlozit}>
                Nová verze…
              </Button>
            </div>
          </>
        ) : (
          <>
            <EmptyState
              eyebrow="Bez brány"
              title="Firma nemá aktivní platební bránu"
              description="Bez ní nejde platit kartou a vozy, jejichž majitelem je tato firma, nelze nabízet na webu."
            />
            <div>
              <Button variant="primary" size="sm" icon={<Icon name="plus" size={12} />} onClick={() => setNovaOpen(true)} disabled={!lzeUlozit}>
                Nastavit bránu…
              </Button>
            </div>
          </>
        )}
      </section>

      {drivejsi.length > 0 && (
        <section className="flex flex-col gap-2" aria-label="Dřívější verze">
          <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Dřívější verze</span>
          <DataTable<PujcovnaBrana> aria-label="Dřívější verze platební brány" variant="bordered" columns={columns} data={drivejsi} getRowId={(b) => String(b.id)} />
        </section>
      )}

      {novaOpen && lzeUlozit && <NovaVerzeModal poskytovatele={poskytovatele} open onOpenChange={(open) => !open && setNovaOpen(false)} />}
      {dialog}
    </div>
  );
}
